import {Typography, Grid} from '@hconnect/uikit'
import {
  Box,
  Button,
  Divider,
  makeStyles,
  Paper,
  Theme,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import {Stack} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import emailImage from '../../Assets/confirmationPage/email.svg'
import {ShippingType} from '../../OrderIntake/declarations/OrderIntake.enums'
import {useFeaturesState} from '../../Organisms/Features'
import {MaterialSectionHeader} from './VisualComponents'
import {ShippingAddress, OrderRequestGroupResponse} from '../declarations/types'
import {ShippingAddress as ShippingAddressComponent} from './ShippingAddress'
import {SplitLoadOrderIntakePayload} from '../BulkCement/containers/SplitLoad/buildSplitLoadPayload'
import {ConfirmationSummaryBox} from './SplitLoadConfirmation/ConfirmationSummaryBox'
import {SplitLoadOrderCard} from './SplitLoadConfirmation/SplitLoadOrderCard'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    [theme.breakpoints.up('sm')]: {
      padding: `${theme.spacing(0)}px ${theme.spacing(4)}px`
    },
    padding: `${theme.spacing(2)}px`
  },
  confirmationBox: {
    background: '#FBFFF9',
    border: '1px solid #BBDEB3',
    padding: '24px',
    borderRadius: '8px'
  },
  confirmationBoxError: {
    background: '#FFF5F5',
    border: '1px solid #FC8181',
    padding: '24px',
    borderRadius: '8px'
  },
  confirmationBoxMixed: {
    background: '#FFFAF0',
    border: '1px solid #F6AD55',
    padding: '24px',
    borderRadius: '8px'
  },
  emailSubjectLogo: {
    height: '35px',
    width: '35px',
    backgroundColor: '#11507B',
    color: 'white',
    borderRadius: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  emailCounter: {
    backgroundColor: 'orange',
    position: 'absolute',
    color: 'white',
    top: '4px',
    right: '6px',
    fontSize: '12px',
    width: '18px',
    height: '18px',
    borderRadius: '10px',
    padding: '0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold'
  },
  paperStyle: {
    [theme.breakpoints.up('md')]: {
      borderRadius: 8
    },
    borderRadius: 0,
    padding: 32
  },
  backToListButton: {
    marginTop: '20px',
    width: '100%',
    '& button': {
      width: '100%'
    }
  }
}))

interface Props {
  orderResponses: OrderRequestGroupResponse[]
  splitLoadPayloads: SplitLoadOrderIntakePayload[]
  onClose: () => void
  userFullName: string
  mainPhone: string
  backupPhone?: string
  shippingType: ShippingType
  deliveryAddress: ShippingAddress
  quantityUom: string
}

const SplitLoadConfirmationScreen: React.FC<Props> = ({
  orderResponses,
  splitLoadPayloads,
  onClose,
  userFullName,
  mainPhone,
  shippingType,
  backupPhone = '',
  deliveryAddress,
  quantityUom
}) => {
  const classes = useStyles()
  const {t} = useTranslation()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const {getFeature} = useFeaturesState()
  const orderIntakeCollectSendingQR = getFeature('OrderIntakeCollectSendingQR')
  const isSlotManagementEnabled = getFeature(
    shippingType === ShippingType.DELIVER ? 'OrderIntakeSlotsDeliver' : 'OrderIntakeSlotsCollect'
  )

  const successfulOrders = orderResponses.filter((order) => order.isSuccess)
  const failedOrders = orderResponses.filter((order) => !order.isSuccess)
  const hasErrors = failedOrders.length > 0
  const allFailed = failedOrders.length === orderResponses.length
  const someFailed = hasErrors && !allFailed

  const getConfirmationBoxClass = () => {
    if (allFailed) return classes.confirmationBoxError
    if (someFailed) return classes.confirmationBoxMixed
    return classes.confirmationBox
  }

  return (
    <Grid container style={{padding: 0}}>
      <Grid item xs={12}>
        <Grid
          container
          className={classes.container}
          justifyContent="space-between"
          data-test-id="split-load-confirmation-screen-container"
          style={{padding: 0}}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Paper
                style={isMobile ? {borderRadius: 0, background: '#F5F8FB', padding: 16} : {}}
                className={classes.paperStyle}
              >
                <Stack spacing={2}>
                  {/* Main Confirmation Box */}
                  <Box>
                    <ConfirmationSummaryBox
                      allFailed={allFailed}
                      someFailed={someFailed}
                      successCount={successfulOrders.length}
                      failedCount={failedOrders.length}
                      totalCount={orderResponses.length}
                      shippingType={shippingType}
                      orderIntakeCollectSendingQR={orderIntakeCollectSendingQR}
                      isSlotManagementEnabled={isSlotManagementEnabled}
                      boxClassName={getConfirmationBoxClass()}
                    />
                  </Box>

                  {/* List of Orders Header */}
                  <Box display="flex" alignItems="center">
                    <MaterialSectionHeader>
                      {t('orderIntake.confirmationScreen.listOfOrderedItems')}
                    </MaterialSectionHeader>
                  </Box>

                  {/* Successful Orders */}
                  {successfulOrders.length > 0 && (
                    <Box>
                      {successfulOrders.map((order, index) => {
                        const payload = splitLoadPayloads[order.index]
                        if (!payload) return null
                        return (
                          <SplitLoadOrderCard
                            key={`success-${index}`}
                            orderResponse={order}
                            payload={payload}
                            index={index}
                            quantityUom={quantityUom}
                          />
                        )
                      })}
                    </Box>
                  )}

                  {/* Failed Orders */}
                  {failedOrders.length > 0 && (
                    <Box>
                      <Typography
                        variant="h4"
                        color="error"
                        style={{marginTop: '16px', marginBottom: '8px'}}
                      >
                        {t('orderIntake.confirmationScreen.failedOrders')}
                      </Typography>
                      {failedOrders.map((order, index) => {
                        const payload = splitLoadPayloads[order.index]
                        if (!payload) return null
                        return (
                          <SplitLoadOrderCard
                            key={`failed-${index}`}
                            orderResponse={order}
                            payload={payload}
                            index={successfulOrders.length + index}
                            quantityUom={quantityUom}
                          />
                        )
                      })}
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Grid>

            {/* Right Sidebar - Contact & Additional Info */}
            <Grid item xs={12} md={4} style={{paddingLeft: 0, paddingRight: 16}}>
              <Paper className={classes.paperStyle}>
                <Box>
                  {/* QR Code Info (for collect orders) */}
                  {shippingType === ShippingType.COLLECT &&
                    orderIntakeCollectSendingQR &&
                    successfulOrders.length > 0 && (
                      <Box marginBottom={4} marginTop={2} padding={2}>
                        <Typography variant="h4">{t('orderIntake.qrCodeSent')}</Typography>

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          marginBottom={2}
                        >
                          <Typography variant="body2">
                            {t('orderIntake.confirmationScreen.qrCodeSentText', {
                              count: successfulOrders.length
                            })}
                          </Typography>
                          <Box position="relative">
                            <img
                              src={emailImage}
                              style={{textAlign: 'center', maxWidth: 50}}
                              alt={t('orderIntake.confirmationScreen.qrCodeSentText', {
                                count: successfulOrders.length
                              })}
                            />
                            <Box className={classes.emailCounter}>{successfulOrders.length}</Box>
                          </Box>
                        </Box>

                        <Divider />
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          gridColumnGap={15}
                          marginTop={2}
                        >
                          <Box>
                            <Box className={classes.emailSubjectLogo}>
                              <Typography variant="body2">HC</Typography>
                            </Box>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="secondary">
                              {t('orderIntake.confirmationScreen.subject')}
                            </Typography>
                            <Typography variant="h4">
                              {t('orderIntake.confirmationScreen.qrCodeForOrder')}
                            </Typography>
                            <Box display="flex">
                              <Typography variant="body2" color="secondary">
                                {t('orderIntake.confirmationScreen.from')}
                              </Typography>
                              <Typography variant="body2">
                                {t('orderIntake.confirmationScreen.fromValue')}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}

                  {/* Additional Information Section */}
                  <Box mb={2}>
                    <Typography variant="h3" color="textPrimary">
                      {t('orderIntake.confirmationScreen.additionalInfo')}
                    </Typography>
                  </Box>

                  {/* Delivery/Collection Address */}
                  <Box mb={1}>
                    <Typography variant="body2" color="secondary">
                      {shippingType === ShippingType.DELIVER
                        ? t('orderIntake.deliverTo')
                        : t('orderIntake.collectionAddress')}
                    </Typography>
                    <ShippingAddressComponent
                      showInLine={false}
                      name={deliveryAddress.siteName}
                      street={deliveryAddress.street}
                      postalCode={deliveryAddress.postalCode}
                      city={deliveryAddress.city}
                    />
                  </Box>

                  {/* Contact Person */}
                  <Box mb={1}>
                    <Typography variant="body2" color="secondary">
                      {t('orderIntake.confirmationScreen.contactPerson')}
                    </Typography>
                    <Typography variant="body2" color="textPrimary">
                      {userFullName}
                    </Typography>
                  </Box>

                  {/* Phone Numbers */}
                  <Box mb={1}>
                    <Typography variant="body2" color="secondary">
                      {t('orderIntake.mainPhone')}
                    </Typography>
                    <Typography variant="body2" color="textPrimary">
                      {mainPhone}
                    </Typography>
                    {backupPhone && mainPhone !== backupPhone && (
                      <>
                        <Typography variant="body2" color="secondary">
                          {t('orderIntake.backupPhone')}
                        </Typography>
                        <Typography variant="body2" color="textPrimary">
                          {backupPhone}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Cancellation Info */}
                <Box mb={1}>
                  <Typography variant="body2" color="secondary">
                    {t('orderIntake.confirmationScreen.cancellation')}
                  </Typography>
                  <Typography color="textPrimary">
                    {t('orderIntake.changesOrCancellationsDisclaimer')}
                  </Typography>
                </Box>

                {/* Back to Orders List Button */}
                <Button
                  data-test-id="split-load-confirmation-screen-close-button"
                  color="primary"
                  className={classes.backToListButton}
                  onClick={() => {
                    onClose()
                  }}
                >
                  {t('orderIntake.confirmationScreen.backToOrdersList')}
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export {SplitLoadConfirmationScreen}
